import { FC, useState, useMemo, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import {
  AvailablePaymentCurrency,
  formatCurrencyAmount,
  getDollarValue,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";
import { useForm, useWatch } from "react-hook-form";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Amount } from "@/icons/outlined";

interface EditDonationAmountDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newAmount: number) => Promise<boolean>;
  isLoading: boolean;
  currentAmount: number;
  currency: AvailablePaymentCurrency;
  recurrenceInterval: SubscriptionRecurrenceInterval | null;
}

const getUpdateToKey = (
  recurrenceInterval: SubscriptionRecurrenceInterval | null
):
  | "donorTransactionDetailDrawer.editAmountUpdateTo.weekly"
  | "donorTransactionDetailDrawer.editAmountUpdateTo.biWeekly"
  | "donorTransactionDetailDrawer.editAmountUpdateTo.monthly"
  | "donorTransactionDetailDrawer.editAmountUpdateTo.quarterly"
  | "donorTransactionDetailDrawer.editAmountUpdateTo.yearly" => {
  switch (recurrenceInterval) {
    case SubscriptionRecurrenceInterval.Weekly:
      return "donorTransactionDetailDrawer.editAmountUpdateTo.weekly";
    case SubscriptionRecurrenceInterval.BiWeekly:
      return "donorTransactionDetailDrawer.editAmountUpdateTo.biWeekly";
    case SubscriptionRecurrenceInterval.Monthly:
      return "donorTransactionDetailDrawer.editAmountUpdateTo.monthly";
    case SubscriptionRecurrenceInterval.Quarterly:
      return "donorTransactionDetailDrawer.editAmountUpdateTo.quarterly";
    case SubscriptionRecurrenceInterval.Yearly:
    default:
      return "donorTransactionDetailDrawer.editAmountUpdateTo.yearly";
  }
};

export const EditDonationAmountDialog: FC<EditDonationAmountDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  currentAmount,
  currency,
  recurrenceInterval,
}) => {
  const { t } = useTranslate();
  const { locale, isoLocale } = useLocaleContext();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<number>(currentAmount);

  const form = useForm<{ newAmount?: number }>({
    defaultValues: { newAmount: currentAmount },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (open) {
      form.reset({ newAmount: currentAmount });
      setConfirmationOpen(false);
    }
  }, [open, currentAmount, form]);

  const currentFormAmount = useWatch({ control: form.control, name: "newAmount" });
  const hasChanged = currentFormAmount !== undefined && currentFormAmount !== currentAmount;

  const formattedCurrentAmount = useMemo(
    () => formatCurrencyAmount({ value: getDollarValue(currentAmount), locale: isoLocale, currency }),
    [currentAmount, isoLocale, currency]
  );

  const formattedPendingAmount = useMemo(
    () => formatCurrencyAmount({ value: getDollarValue(pendingAmount), locale: isoLocale, currency }),
    [pendingAmount, isoLocale, currency]
  );

  const mainButtonLabel = useMemo(() => {
    if (!hasChanged || !currentFormAmount) {
      return t("dashboard", "donorTransactionDetailDrawer.editAmountSave");
    }
    const formattedAmount = formatCurrencyAmount({
      value: getDollarValue(currentFormAmount),
      locale: isoLocale,
      currency,
    });
    return t("dashboard", getUpdateToKey(recurrenceInterval), { amount: formattedAmount });
  }, [hasChanged, currentFormAmount, isoLocale, currency, recurrenceInterval, t]);

  const handleOpenConfirmation = form.handleSubmit(({ newAmount }) => {
    if (!newAmount) {
      return;
    }
    setPendingAmount(newAmount);
    setConfirmationOpen(true);
  });

  const handleConfirm = async () => {
    const success = await onConfirm(pendingAmount);
    if (success) {
      setConfirmationOpen(false);
      onClose();
    }
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleClose = () => {
    form.reset({ newAmount: currentAmount });
    setConfirmationOpen(false);
    onClose();
  };

  return (
    <>
      <DashboardDialog open={open && !confirmationOpen} onClose={handleClose} maxWidth="xs" vibe="brand">
        <DashboardDialogBody
          slots={{
            header: (
              <DashboardDialogHeader
                icon={<Amount />}
                title={t("dashboard", "donorTransactionDetailDrawer.editAmountTitle")}
              />
            ),
            content: (
              <DashboardDialogContent>
                <form>
                  <Grid container>
                    <Grid item xs={8}>
                      <DashboardAmountInput
                        control={form.control}
                        locale={locale}
                        currency={currency}
                        name="newAmount"
                        label={t("dashboard", "editDonationAmountLabel")}
                        placeholder={t("dashboard", "editDonationAmountPlaceholder", {
                          amount: formattedCurrentAmount,
                        })}
                        defaultValue={currentAmount}
                      />
                    </Grid>
                  </Grid>
                </form>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton onClick={handleOpenConfirmation}>{mainButtonLabel}</DashboardButton>
                <DashboardButton onClick={handleClose}>{t("dashboard", "common.cancel")}</DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>

      <DashboardDialog open={confirmationOpen} onClose={handleCloseConfirmation} maxWidth="xs" vibe="brand">
        <DashboardDialogBody
          slots={{
            header: <DashboardDialogHeader title={t("dashboard", "editDonationAmountConfirm")} />,
            content: (
              <DashboardDialogContent>
                <Stack spacing={2}>
                  <DashboardTypography variant="body2">
                    {t("dashboard", "editDonationAmountConfirmContentDonor", {
                      amount: formattedPendingAmount,
                    })}
                  </DashboardTypography>
                  <DashboardInfobox vibe="brand">
                    {t("dashboard", "editDonationAmountConfirmAlertDonor")}
                  </DashboardInfobox>
                </Stack>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton onClick={handleConfirm} isLoading={isLoading}>
                  {t("dashboard", "common.confirm")}
                </DashboardButton>
                <DashboardButton onClick={handleCloseConfirmation}>{t("dashboard", "common.cancel")}</DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>
    </>
  );
};
